// src/App.tsx
import React from "react";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { CssBaseline, ThemeProvider, Box, Typography, Button, TextField } from "@mui/material";
import { SnackbarProvider } from "./context/SnackbarContext";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterMoment } from "@mui/x-date-pickers-pro/AdapterMoment";
import { lightTheme, darkTheme } from "./theme/theme";
import { ThemeModeProvider, useThemeMode } from "./context/ThemeContext";
import { PostHogErrorBoundary, type PostHogErrorBoundaryFallbackProps } from "posthog-js/react";
import { usePostHog } from "posthog-js/react";

import 'simplebar-react/dist/simplebar.min.css';
import Router from "./routes/sections";

const ThemedApp: React.FC = () => {
  const { isDarkMode } = useThemeMode();
  
  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <BrowserRouter>
          <CssBaseline />
          <Router />
        </BrowserRouter>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

const ErrorFallback: React.FC<PostHogErrorBoundaryFallbackProps> = ({ error: rawError }) => {
  const error = rawError instanceof Error ? rawError : new Error(String(rawError));
  const posthog = usePostHog();
  const [feedback, setFeedback] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);

  // Detect dark mode from system preference
  const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const theme = prefersDarkMode ? darkTheme : lightTheme;

  const handleSubmitFeedback = () => {
    if (feedback.trim()) {
      posthog.capture('user_error_feedback', {
        message: feedback,
        error: error.message,
        stack: error.stack
      });
      setSubmitted(true);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          p: 3,
          maxWidth: 600,
          mx: "auto"
        }}>
        <Typography variant="h4" gutterBottom>
          Something went wrong
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 1
          }}>
          Please try refreshing the page. If the problem persists, let us know what happened.
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 3,
            fontFamily: "monospace"
          }}>
          {error.message}
        </Typography>

        {!submitted && (
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Describe what you were doing when this happened (optional)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        {submitted && (
          <Typography
            variant="body2"
            sx={{
              color: "success.main",
              mb: 2
            }}>
            Thank you for your feedback!
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 2
          }}>
          <Button variant="contained" onClick={handleRefresh}>
            Refresh Page
          </Button>
          {!submitted && feedback.trim() && (
            <Button variant="outlined" onClick={handleSubmitFeedback}>
              Submit Feedback
            </Button>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <PostHogErrorBoundary fallback={ErrorFallback}>
      <AuthProvider>
        <SnackbarProvider>
          <ThemeModeProvider>
            <ThemedApp />
          </ThemeModeProvider>
        </SnackbarProvider>
      </AuthProvider>
    </PostHogErrorBoundary>
  );
};

export default App;
